/******************************************************************************
 * FILE: src/modules/wms/services/wmsChecklistService.js
 * MODULE: Work Management System (WMS)
 * FEATURE: Checklist Management API Service
 *
 * PURPOSE:
 * Frontend API client for Checklist Management
 *
 * BACKEND:
 * /api/wms/checklists
 *****************************************************************************/

import axiosClient from "../../../api/axiosClient";

/**
 * ----------------------------------------------------------------------
 * LIST CHECKLISTS
 * ----------------------------------------------------------------------
 * Backend:
 * GET /api/wms/checklists
 */
async function listChecklists(params = {}) {
  const res = await axiosClient.get(
    "/api/wms/checklists",
    { params }
  );

  const payload = res.data?.data || {};

  return {
    items: payload.data || [],
    pagination: payload.pagination || {},
  };
}

/**
 * ----------------------------------------------------------------------
 * GET CHECKLIST
 * ----------------------------------------------------------------------
 * Backend:
 * GET /api/wms/checklists/:id
 */
async function getChecklist(checklistId) {
  const res = await axiosClient.get(
    `/api/wms/checklists/${checklistId}`
  );

  return res.data.data;
}

/**
 * ----------------------------------------------------------------------
 * CREATE CHECKLIST
 * ----------------------------------------------------------------------
 * Backend:
 * POST /api/wms/checklists
 */
async function createChecklist(payload) {
  const res = await axiosClient.post(
    "/api/wms/checklists",
    payload
  );

  return res.data.data;
}

/**
 * ----------------------------------------------------------------------
 * UPDATE CHECKLIST
 * ----------------------------------------------------------------------
 * Backend:
 * PUT /api/wms/checklists/:id
 */
async function updateChecklist(
  checklistId,
  payload
) {
  const res = await axiosClient.put(
    `/api/wms/checklists/${checklistId}`,
    payload
  );

  return res.data.data;
}

/**
 * ----------------------------------------------------------------------
 * DELETE CHECKLIST
 * ----------------------------------------------------------------------
 * Backend:
 * DELETE /api/wms/checklists/:id
 */
async function deleteChecklist(checklistId) {
  const res = await axiosClient.delete(
    `/api/wms/checklists/${checklistId}`
  );

  return res.data;
}

/**
 * ----------------------------------------------------------------------
 * GET SECTIONS
 * ----------------------------------------------------------------------
 * Backend:
 * GET /api/wms/checklists/:id/sections
 */
async function getSections(checklistId) {
  const res = await axiosClient.get(
    `/api/wms/checklists/${checklistId}/sections`
  );

  return res.data.data || [];
}

/**
 * ----------------------------------------------------------------------
 * CREATE SECTION
 * ----------------------------------------------------------------------
 * Backend:
 * POST /api/wms/checklists/:id/sections
 */
async function createSection(
  checklistId,
  payload
) {
  const res = await axiosClient.post(
    `/api/wms/checklists/${checklistId}/sections`,
    payload
  );

  return res.data.data;
}

/**
 * ----------------------------------------------------------------------
 * UPDATE SECTION
 * ----------------------------------------------------------------------
 * Backend:
 * PUT /api/wms/checklists/:checklistId/sections/:sectionId
 */
async function updateSection(
  checklistId,
  sectionId,
  payload
) {
  const res = await axiosClient.put(
    `/api/wms/checklists/${checklistId}/sections/${sectionId}`,
    payload
  );

  return res.data.data;
}

/**
 * ----------------------------------------------------------------------
 * DELETE SECTION
 * ----------------------------------------------------------------------
 * Backend:
 * DELETE /api/wms/checklists/:checklistId/sections/:sectionId
 */
async function deleteSection(
  checklistId,
  sectionId
) {
  const res = await axiosClient.delete(
    `/api/wms/checklists/${checklistId}/sections/${sectionId}`
  );

  return res.data;
}

/**
 * ----------------------------------------------------------------------
 * GET ITEMS
 * ----------------------------------------------------------------------
 * Backend:
 * GET /api/wms/checklists/sections/:sectionId/items
 */
async function getItems(sectionId) {
  const res = await axiosClient.get(
    `/api/wms/checklists/sections/${sectionId}/items`
  );

  return res.data.data || [];
}

/**
 * ----------------------------------------------------------------------
 * CREATE ITEM
 * ----------------------------------------------------------------------
 * Backend:
 * POST /api/wms/checklists/sections/:sectionId/items
 */
async function createItem(
  sectionId,
  payload
) {
  const res = await axiosClient.post(
    `/api/wms/checklists/sections/${sectionId}/items`,
    payload
  );

  return res.data.data;
}

/**
 * ----------------------------------------------------------------------
 * UPDATE ITEM
 * ----------------------------------------------------------------------
 * Backend:
 * PUT /api/wms/checklists/sections/:sectionId/items/:itemId
 */
async function updateItem(
  sectionId,
  itemId,
  payload
) {
  const res = await axiosClient.put(
    `/api/wms/checklists/sections/${sectionId}/items/${itemId}`,
    payload
  );

  return res.data.data;
}

/**
 * ----------------------------------------------------------------------
 * DELETE ITEM
 * ----------------------------------------------------------------------
 * Backend:
 * DELETE /api/wms/checklists/sections/:sectionId/items/:itemId
 */
async function deleteItem(
  sectionId,
  itemId
) {
  const res = await axiosClient.delete(
    `/api/wms/checklists/sections/${sectionId}/items/${itemId}`
  );

  return res.data;
}

/**
 * ----------------------------------------------------------------------
 * GET OPTIONS
 * ----------------------------------------------------------------------
 * Backend:
 * GET /api/wms/checklists/items/:itemId/options
 */
async function getOptions(itemId) {
  const res = await axiosClient.get(
    `/api/wms/checklists/items/${itemId}/options`
  );

  return res.data.data || [];
}

/**
 * ----------------------------------------------------------------------
 * CREATE OPTION
 * ----------------------------------------------------------------------
 * Backend:
 * POST /api/wms/checklists/items/:itemId/options
 */
async function createOption(
  itemId,
  payload
) {
  const res = await axiosClient.post(
    `/api/wms/checklists/items/${itemId}/options`,
    payload
  );

  return res.data.data;
}

/**
 * ----------------------------------------------------------------------
 * UPDATE OPTION
 * ----------------------------------------------------------------------
 * Backend:
 * PUT /api/wms/checklists/items/:itemId/options/:optionId
 */
async function updateOption(
  itemId,
  optionId,
  payload
) {
  const res = await axiosClient.put(
    `/api/wms/checklists/items/${itemId}/options/${optionId}`,
    payload
  );

  return res.data.data;
}

/**
 * ----------------------------------------------------------------------
 * DELETE OPTION
 * ----------------------------------------------------------------------
 * Backend:
 * DELETE /api/wms/checklists/items/:itemId/options/:optionId
 */
async function deleteOption(
  itemId,
  optionId
) {
  const res = await axiosClient.delete(
    `/api/wms/checklists/items/${itemId}/options/${optionId}`
  );

  return res.data;
}

/**
 * ----------------------------------------------------------------------
 * EXPORT
 * ----------------------------------------------------------------------
 */
const wmsChecklistService = {
  listChecklists,
  getChecklist,

  createChecklist,
  updateChecklist,
  deleteChecklist,

  getSections,
  createSection,
  updateSection,
  deleteSection,

  getItems,
  createItem,
  updateItem,
  deleteItem,

  getOptions,
  createOption,
  updateOption,
  deleteOption,
};

export default wmsChecklistService;